import { html, View } from 'rune-ts';
import { MembershipTypeS, MembershipUtilS } from '../../../../features/Membership/outbound/share';
import { ButtonAction } from '../../../../shared/components/atoms/ButtonAction/ButtonAction';
import klass from './CreatorMainProfile.module.scss';
import { CreatorConstantS, CreatorTypeS } from '../../../../features/Creator/outbound/share';
import { AdultVerificationIcon18Plus, AdultVerificationIcon19 } from '../../atoms/Icon';
import { htmlIf } from '../../../../shared/util';
import { getSnsIcon } from '../../../../shared/util/snsIcon';
import { getResizedUrl } from '../../../../shared/util/image';
import { commify } from '../../../../shared/util/commify';
import { BecomeFanButtonView } from './views/BecomeFanButtonView';
import { MShopAppStoreAboutEditModalMuiF } from '../../../../../../modules/MShop/App/Store/About/EditModal/F/Mui/module/MShopAppStoreAboutEditModalMuiF';
import { MuiF } from '../../../../../../modules/Mui/F/Function/module/MuiF';
import { ExpandableText } from '../ExpandableText/ExpandableText';
import { humanReadableNumberV2 } from '../../../../../../shared/utils/formatter';
import { makePlusUrl } from '@services/MarppleShop/shared/util/url.ts';
import { getSharedData } from '@services/MarppleShop/shared/util/rune.ts';
import { type LocalsStoreMembership } from '@services/MarppleShop/shared/type/locals.ts';
import { font, staticFont } from '@services/MarppleShop/shared/typography';
import { RankDiff } from '@services/MarppleShop/renderApp/components/atoms/RankDiff/RankDiff.ts';

type CreatorMainProfileViewData = {
  creator: CreatorTypeS.CreatorMainProfile;
  is_mobile: boolean;
  is_store_owner?: boolean;
  is_login: boolean;
  color_set: 'white' | 'black';
  membership?: LocalsStoreMembership & {
    my: {
      token?: string;
      memberships?: MembershipTypeS.MembershipSummary[];
    };
  };
};

interface CreatorMainProfileViewOption {
  klass?: string;
  adult_check_needed?: boolean;
  is_mobile: boolean;
}

export class FanCountView extends View<{ like_count: number; font?: string }> {
  protected override template() {
    const { font = staticFont('bebas_20_bold', 'bebas_24_bold') } = this.data;
    const { value, unit } = humanReadableNumberV2.to(this.data.like_count, ET.lang == 'kr');
    return html`<div class="${font}">${value}</div>
      ${unit ? html`<div>${unit}</div>` : ''}`;
  }

  public plusCount() {
    ++this.data.like_count;
    this.redraw();
  }

  public minusCount() {
    --this.data.like_count;
    this.redraw();
  }
}

export class CreatorMainProfileView extends View<CreatorMainProfileViewData> {
  private static snsIcon(sns_url: string | null) {
    if (!sns_url) return '';
    const { tmpl, link } = getSnsIcon(sns_url);
    return html` <a href="${link}" target="_blank">${tmpl}</a> `;
  }

  private state: CreatorMainProfileViewOption;
  private becomeFanButtonView = new BecomeFanButtonView({
    is_liked: this.data.creator.is_liked,
    store_id: this.data.creator.id,
    is_login: this.data.is_login,
  });

  public getBecomeFanButtonView(): BecomeFanButtonView {
    return this.becomeFanButtonView;
  }

  private fanCountView = new FanCountView({ like_count: this.data.creator.like_count });

  public getFanCountView(): FanCountView {
    return this.fanCountView;
  }

  constructor(data: CreatorMainProfileViewData, option: CreatorMainProfileViewOption) {
    super(data);
    this.state = {
      klass: option.klass,
      adult_check_needed: option.adult_check_needed,
      is_mobile: option.is_mobile,
    };
  }

  override template() {
    const {
      creator: {
        thumbnail_img,
        is_adult,
        name,
        description /* 팬 */,
        fan_letter_count,
        domain_name,
        ranking,
      },
      is_store_owner,
      is_mobile,
    } = this.data;

    const creator_thumbnail_img = thumbnail_img || CreatorConstantS.thumbnail_img;
    const { value: fan_letter_value, unit: fan_letter_unit } = humanReadableNumberV2.to(
      fan_letter_count,
      ET.lang == 'kr',
    );
    const COLOR_SET = {
      white: klass.creator_main_profile_white,
      black: klass.creator_main_profile_black,
    };
    return html`
      <div>
        <div
          class="${htmlIf(
            this.state.klass ?? '',
            !!this.state.klass,
          )} ${klass.creator_main_profile} ${COLOR_SET[this.data.color_set]} ${font('14_medium')}"
        >
          <div class="${klass.left}">
            <div class="${klass.profile_image}">
              <div class="${klass.logo}">
                ${is_adult && this.state.adult_check_needed
                  ? html`
                      <div class="${klass.adult}">
                        ${ET.lang === 'kr' ? AdultVerificationIcon19 : AdultVerificationIcon18Plus}
                      </div>
                    `
                  : html`
                      <img
                        src="${getResizedUrl({
                          url: creator_thumbnail_img,
                          width: is_mobile ? 144 * 2 : 156 * 2,
                          format: 'webp',
                          quality: 80,
                        })}"
                        alt="${name}"
                      />
                    `}
              </div>
              <div class="${klass.profile_image_button}">
                ${is_store_owner ? this._makeAboutForOwner() : this.becomeFanButtonView}
              </div>
            </div>
            <div class="${klass.info}">
              <div class="${klass.header}">
                <div class="${klass.creator_name} ${font('36_bold', '24_bold')}">
                  ${html.preventEscape(name ?? '')}
                </div>
                ${this._makeAbout()}
              </div>
              <div class="${klass.body}">
                <div class="${klass.body_left}">
                  <div class="${klass.counts}">
                    <div class="${klass.count}">
                      <div class="${klass.count_title} ${font('16_medium', '12_medium')}">
                        ${ET('creator_shop::profile::fan')}
                      </div>
                      <div class="${klass.count_value}">${this.fanCountView}</div>
                    </div>
                    <div class="${klass.count_bar}"></div>
                    <a href="/${ET.lang}/${domain_name}/fan-letter" class="${klass.count}">
                      <div class="${klass.count_title} ${font('16_medium', '12_medium')}">
                        ${ET('creator_shop::fan_letters::title')}
                      </div>
                      <div class="${klass.count_value}">
                        <div class="${staticFont('bebas_20_bold', 'bebas_24_bold')}">${fan_letter_value}</div>
                        ${fan_letter_unit ? html`<div>${fan_letter_unit}</div>` : ''}
                      </div>
                    </a>
                    ${!ranking
                      ? ``
                      : html`
                          <div class="${klass.count_bar}"></div>
                          <div class="${klass.count}">
                            <div class="${klass.count_title} ${font('16_medium', '12_medium')}">
                              ${is_mobile
                                ? ET('creator_shop::profile::popular_title_mo')
                                : ET('creator_shop::profile::popular_title')}
                            </div>
                            <div class="${klass.count_value}">${this._makeRanking()}</div>
                          </div>
                        `}
                  </div>
                  ${description
                    ? new ExpandableText({
                        text: description.replaceAll('\n', '<br/>'),
                        textKlass: `${klass.description} ${font('14_medium')}`,
                        buttonKlass: `${klass.expand_button} ${font('14_medium')}`,
                        buttonLabelMore: ET('creator_shop::profile::expand_more'),
                        buttonLabelLess: ET('creator_shop::profile::expand_less'),
                        lineClamp: 2,
                      })
                    : ''}
                </div>
                <div class="${klass.body_right}">
                  <div class="${klass.buttons}">
                    ${this._makeMembershipPlanButton()}${this._makeMembershipSupportButton()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private _makeAbout() {
    const {
      creator: { sns_url },
      membership,
    } = this.data;
    if (!sns_url && !membership?.ciety) {
      return '';
    }
    return html`<div class="${klass.about}">
      ${sns_url && !(sns_url?.indexOf('ciety') != -1)
        ? // 사이어티를 연동한 경우에는 사이어티 아이콘은 하나만 보여줌
          html`<div class="${klass.about_sns}">${CreatorMainProfileView.snsIcon(sns_url)}</div>`
        : ''}
      ${membership?.ciety
        ? html`<div class="${klass.about_sns}">
            ${CreatorMainProfileView.snsIcon(MembershipUtilS.getCietyUrl(`/@${membership.ciety.subdomain}`))}
          </div>`
        : ''}
    </div>`;
  }

  private _makeAboutForOwner() {
    return html`
      <div class="${klass.about_edit}">
        <button type="button" class="${klass.about_btn_edit}">${about_btn_edit_svg}</button>
      </div>
    `;
  }

  private _makeRanking() {
    const { ranking } = this.data.creator;
    if (!ranking) {
      return html.preventEscape(
        ET('creator_shop::profile::rank', {
          rank: '-',
          klass: font('16_bold', '20_bold'),
        }),
      );
    }

    return html`${html.preventEscape(
      ET('creator_shop::profile::rank', {
        rank: `<div class="${staticFont('bebas_20_bold', 'bebas_24_bold')}">${commify(ranking.current_rank)}</div>`,
        klass: font('16_bold'),
      }),
    )}
    ${new RankDiff({ ranking, klass: klass.rank_diff, font: staticFont('unica_12_bold') })}`;
  }

  private _makeMembershipPlanButton() {
    const { membership, creator } = this.data;
    const show_membership_plan =
      !!membership?.id &&
      membership?.membership_plan_existed &&
      !(creator.is_adult && this.state.adult_check_needed);

    if (!show_membership_plan) {
      return '';
    }

    const { memberships: my_memberships } = membership.my || {};

    const label =
      !my_memberships?.length || !my_memberships[0]
        ? ET('mps2::membership::go_join_button_label')
        : ET('mps2::membership::go_join_button_label_already');

    const sharedData = getSharedData(this);
    return new ButtonAction({
      label,
      type: 'white',
      size: 'medium',
      klass: klass.membership,
      href: makePlusUrl(
        sharedData?.plus_crew?.domain_name,
        `/${ET.lang}/${creator.domain_name}/membership/main`,
      ),
    });
  }

  private _makeMembershipSupportButton() {
    const { membership, creator } = this.data;
    const show_membership_support =
      !!membership?.id &&
      membership?.membership_support_existed &&
      !(creator.is_adult && this.state.adult_check_needed);

    if (!show_membership_support) {
      return '';
    }

    const sharedData = getSharedData(this);
    return new ButtonAction({
      label: ET('mps2::membership::support::go_support_button_label'),
      type: 'white',
      size: 'medium',
      klass: klass.membership,
      href: makePlusUrl(
        sharedData?.plus_crew?.domain_name,
        `/${ET.lang}/${creator.domain_name}/membership/support`,
      ),
    });
  }

  protected override onRender() {
    this.delegate('click', `.${klass.about_btn_edit}`, () => {
      return MuiF.openFrame(
        MShopAppStoreAboutEditModalMuiF.frame,
        (_f: unknown, _p: unknown, [t]: [{ makeData: () => { store: CreatorTypeS.CreatorMainProfile } }]) => {
          t.makeData = () => ({ store: this.data.creator });
        },
      );
    });
  }
}

const about_btn_edit_svg =
  html.preventEscape(`<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
<g clip-path="url(#clip0_1128_230489)">
<path d="M14.06 5.06989L10.9226 1.92772C10.4863 1.49077 9.72281 1.49077 9.28651 1.92772L5.13534 6.08515L1.94015 9.28515C1.722 9.50362 1.6001 9.79278 1.6001 10.1012V13.2434C1.6001 13.886 2.1198 14.4 2.75498 14.4H5.89243C6.2004 14.4 6.48912 14.2779 6.71368 14.0594L14.06 6.70201C14.2782 6.48354 14.4001 6.19438 14.4001 5.87952C14.4001 5.56466 14.2782 5.28193 14.06 5.05703V5.06989ZM11.9748 7.20322L8.78606 4.00964L10.0757 2.71808H10.127L13.2645 5.91165L11.9748 7.20322Z" fill="currentColor"/>
</g>
<defs>
<clipPath id="clip0_1128_230489">
<rect width="16" height="16" fill="currentColor"/>
</clipPath>
</defs>
</svg>`);
